import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import Select, { SelectProps } from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Table from '@cloudscape-design/components/table';
import Textarea from '@cloudscape-design/components/textarea';
import {
  getEvaluationReview,
  getEvaluationReviews,
  updateEvaluationReview,
} from '../services/evaluation';
import type {
  EvaluationReviewDetail,
  EvaluationReviewStatus,
  EvaluationReviewSummary,
  EvaluationReviewUpdate,
} from '../types';

const REVIEW_STATUS_OPTIONS: SelectProps.Option[] = [
  { label: 'Pending review', value: 'pending_review' },
  { label: 'Approved', value: 'approved' },
  { label: 'Needs revision', value: 'needs_revision' },
];

const REVIEW_FILTER_OPTIONS: SelectProps.Option[] = [
  ...REVIEW_STATUS_OPTIONS,
  { label: 'All statuses', value: 'all' },
];

interface ReviewFormState {
  status: SelectProps.Option;
  reviewerNotes: string;
  scorecardAdjustments: string;
  calibratedOverallScore: string;
  feedbackOverride: string;
}

function getStatusOption(status: EvaluationReviewStatus | 'all'): SelectProps.Option {
  return (
    REVIEW_FILTER_OPTIONS.find((option) => option.value === status) ||
    REVIEW_STATUS_OPTIONS[0]
  );
}

function getStatusIndicatorType(status: EvaluationReviewStatus): 'pending' | 'success' | 'warning' {
  if (status === 'approved') return 'success';
  if (status === 'needs_revision') return 'warning';
  return 'pending';
}

function formatDate(value?: string): string {
  if (!value) return 'N/A';
  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function buildFormState(detail: EvaluationReviewDetail): ReviewFormState {
  const review = detail.evaluationReview;
  return {
    status: getStatusOption(review.status),
    reviewerNotes: review.reviewerNotes || '',
    scorecardAdjustments: review.scorecardAdjustments || '',
    calibratedOverallScore:
      review.calibratedOverallScore === null || review.calibratedOverallScore === undefined
        ? ''
        : String(review.calibratedOverallScore),
    feedbackOverride: review.feedbackOverride || '',
  };
}

export function AdminEvaluationReviewPage(): React.ReactElement {
  const [statusFilter, setStatusFilter] = useState<SelectProps.Option>(
    getStatusOption('pending_review')
  );
  const [reviews, setReviews] = useState<EvaluationReviewSummary[]>([]);
  const [selectedReviews, setSelectedReviews] = useState<EvaluationReviewSummary[]>([]);
  const [selectedDetail, setSelectedDetail] = useState<EvaluationReviewDetail | null>(null);
  const [formState, setFormState] = useState<ReviewFormState | null>(null);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const selectedReview = selectedReviews[0];

  const loadReviews = useCallback(async () => {
    setLoadingReviews(true);
    setError(null);
    try {
      const data = await getEvaluationReviews(
        (statusFilter.value || 'pending_review') as EvaluationReviewStatus | 'all'
      );
      setReviews(data);
      setSelectedReviews((current) => {
        if (!current[0]) return [];
        return data.some(
          (review) =>
            review.userId === current[0].userId &&
            review.sessionId === current[0].sessionId
        )
          ? current
          : [];
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load evaluation reviews');
    } finally {
      setLoadingReviews(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  useEffect(() => {
    async function loadDetail() {
      if (!selectedReview) {
        setSelectedDetail(null);
        setFormState(null);
        return;
      }

      setLoadingDetail(true);
      setError(null);
      try {
        const detail = await getEvaluationReview(selectedReview.userId, selectedReview.sessionId);
        setSelectedDetail(detail);
        setFormState(buildFormState(detail));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load evaluation review');
      } finally {
        setLoadingDetail(false);
      }
    }

    loadDetail();
  }, [selectedReview]);

  const calibratedScoreError = useMemo(() => {
    if (!formState?.calibratedOverallScore) return '';
    const value = Number(formState.calibratedOverallScore);
    if (!Number.isFinite(value) || value < 0 || value > 100) {
      return 'Enter a score from 0 to 100.';
    }
    return '';
  }, [formState]);

  const buildUpdate = useCallback(
    (statusOverride?: EvaluationReviewStatus): EvaluationReviewUpdate | null => {
      if (!formState) return null;

      const score = formState.calibratedOverallScore.trim();
      return {
        status: statusOverride || (formState.status.value as EvaluationReviewStatus),
        reviewerNotes: formState.reviewerNotes,
        scorecardAdjustments: formState.scorecardAdjustments,
        calibratedOverallScore: score ? Number(score) : null,
        feedbackOverride: formState.feedbackOverride,
      };
    },
    [formState]
  );

  const saveReview = useCallback(
    async (statusOverride?: EvaluationReviewStatus) => {
      if (!selectedDetail || calibratedScoreError) return;
      const update = buildUpdate(statusOverride);
      if (!update) return;

      setSaving(true);
      setError(null);
      setSuccess(null);
      try {
        const updated = await updateEvaluationReview(
          selectedDetail.userId,
          selectedDetail.sessionId,
          update
        );
        setSelectedDetail(updated);
        setFormState(buildFormState(updated));
        setSuccess('Evaluation review saved.');
        await loadReviews();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save evaluation review');
      } finally {
        setSaving(false);
      }
    },
    [buildUpdate, calibratedScoreError, loadReviews, selectedDetail]
  );

  return (
    <ContentLayout
      data-testid="admin-evaluation-review-page"
      header={
        <Header
          variant="h1"
          description="Approve generated evaluations and capture scorecard calibration notes"
        >
          Evaluation Reviews
        </Header>
      }
    >
      <SpaceBetween size="l">
        {error && (
          <Alert type="error" dismissible onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert type="success" dismissible onDismiss={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Table
          data-testid="evaluation-review-table"
          loading={loadingReviews}
          loadingText="Loading evaluation reviews..."
          selectionType="single"
          selectedItems={selectedReviews}
          onSelectionChange={({ detail }) => setSelectedReviews(detail.selectedItems)}
          trackBy={(item) => `${item.userId}:${item.sessionId}`}
          items={reviews}
          columnDefinitions={[
            {
              id: 'date',
              header: 'Date',
              cell: (item) => formatDate(item.date),
              sortingField: 'date',
              width: 180,
            },
            {
              id: 'scenario',
              header: 'Scenario',
              cell: (item) => item.scenarioName,
              sortingField: 'scenarioName',
              width: 260,
            },
            {
              id: 'role',
              header: 'Role',
              cell: (item) => item.role,
              sortingField: 'role',
              width: 180,
            },
            {
              id: 'score',
              header: 'Score',
              cell: (item) => item.overallScore,
              sortingField: 'overallScore',
              width: 100,
            },
            {
              id: 'status',
              header: 'Review status',
              cell: (item) => (
                <StatusIndicator type={getStatusIndicatorType(item.reviewStatus)}>
                  {getStatusOption(item.reviewStatus).label}
                </StatusIndicator>
              ),
              sortingField: 'reviewStatus',
              width: 160,
            },
          ]}
          header={
            <Header
              variant="h2"
              counter={`(${reviews.length})`}
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <Select
                    selectedOption={statusFilter}
                    onChange={({ detail }) => {
                      setStatusFilter(detail.selectedOption);
                      setSelectedReviews([]);
                    }}
                    options={REVIEW_FILTER_OPTIONS}
                    selectedAriaLabel="Selected"
                  />
                  <Button iconName="refresh" onClick={loadReviews}>
                    Refresh
                  </Button>
                </SpaceBetween>
              }
            >
              Review Queue
            </Header>
          }
          empty={
            <Box textAlign="center" color="inherit">
              No evaluation reviews found.
            </Box>
          }
        />

        {selectedDetail && formState && (
          <Container
            data-testid="evaluation-review-detail"
            header={
              <Header
                variant="h2"
                actions={
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button
                      loading={saving}
                      disabled={!!calibratedScoreError}
                      onClick={() => saveReview()}
                    >
                      Save review
                    </Button>
                    <Button
                      variant="primary"
                      loading={saving}
                      disabled={!!calibratedScoreError}
                      onClick={() => saveReview('approved')}
                    >
                      Approve
                    </Button>
                  </SpaceBetween>
                }
              >
                {selectedDetail.scenarioName}
              </Header>
            }
          >
            <SpaceBetween size="l">
              <ColumnLayout columns={4} variant="text-grid">
                <Box>
                  <Box variant="awsui-key-label">Session</Box>
                  <Box>{selectedDetail.sessionId}</Box>
                </Box>
                <Box>
                  <Box variant="awsui-key-label">Original score</Box>
                  <Box>{selectedDetail.evaluation.overallScore}</Box>
                </Box>
                <Box>
                  <Box variant="awsui-key-label">Status</Box>
                  <StatusIndicator type={getStatusIndicatorType(formState.status.value as EvaluationReviewStatus)}>
                    {formState.status.label}
                  </StatusIndicator>
                </Box>
                <Box>
                  <Box variant="awsui-key-label">Evaluated</Box>
                  <Box>{formatDate(selectedDetail.evaluation.evaluatedAt)}</Box>
                </Box>
              </ColumnLayout>

              <ColumnLayout columns={2}>
                <SpaceBetween size="m">
                  <FormField label="Review status">
                    <Select
                      selectedOption={formState.status}
                      onChange={({ detail }) =>
                        setFormState((current) =>
                          current ? { ...current, status: detail.selectedOption } : current
                        )
                      }
                      options={REVIEW_STATUS_OPTIONS}
                      selectedAriaLabel="Selected"
                    />
                  </FormField>
                  <FormField label="Calibrated overall score" errorText={calibratedScoreError}>
                    <Input
                      type="number"
                      value={formState.calibratedOverallScore}
                      onChange={({ detail }) =>
                        setFormState((current) =>
                          current
                            ? { ...current, calibratedOverallScore: detail.value }
                            : current
                        )
                      }
                    />
                  </FormField>
                  <FormField label="Reviewer notes">
                    <Textarea
                      value={formState.reviewerNotes}
                      rows={5}
                      onChange={({ detail }) =>
                        setFormState((current) =>
                          current ? { ...current, reviewerNotes: detail.value } : current
                        )
                      }
                    />
                  </FormField>
                  <FormField label="Scorecard adjustments">
                    <Textarea
                      value={formState.scorecardAdjustments}
                      rows={5}
                      onChange={({ detail }) =>
                        setFormState((current) =>
                          current
                            ? { ...current, scorecardAdjustments: detail.value }
                            : current
                        )
                      }
                    />
                  </FormField>
                  <FormField label="Feedback override">
                    <Textarea
                      value={formState.feedbackOverride}
                      rows={5}
                      onChange={({ detail }) =>
                        setFormState((current) =>
                          current ? { ...current, feedbackOverride: detail.value } : current
                        )
                      }
                    />
                  </FormField>
                </SpaceBetween>

                <SpaceBetween size="m">
                  <Container header={<Header variant="h3">Evaluation Feedback</Header>}>
                    <SpaceBetween size="s">
                      <Box variant="p">{selectedDetail.evaluation.feedback}</Box>
                      {selectedDetail.evaluation.categories.map((category) => (
                        <Box key={category.name}>
                          <Box variant="awsui-key-label">
                            {category.name}: {category.score}/{category.maxScore}
                          </Box>
                          <Box variant="small">{category.feedback}</Box>
                        </Box>
                      ))}
                    </SpaceBetween>
                  </Container>

                  <Container
                    header={
                      <Header variant="h3" description={`${selectedDetail.transcript.length} characters`}>
                        Transcript
                      </Header>
                    }
                  >
                    <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                      {selectedDetail.transcript || 'No transcript captured.'}
                    </pre>
                  </Container>
                </SpaceBetween>
              </ColumnLayout>
            </SpaceBetween>
          </Container>
        )}

        {loadingDetail && (
          <Container>
            <Box textAlign="center">Loading selected review...</Box>
          </Container>
        )}
      </SpaceBetween>
    </ContentLayout>
  );
}

export default AdminEvaluationReviewPage;
